import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-gradient-to-b from-black/50 via-black/30 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-accent-500 flex items-center justify-center">
            <span className="text-background-50 font-heading text-sm font-bold">H</span>
          </div>
          <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>
            hoobuy<span className="text-accent-400">.beauty</span>
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50 hover:text-accent-300'}`}
          >
            Início
          </Link>

          {/* Category Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50 hover:text-accent-300'}`}
            >
              Categorias
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 w-64 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/artigo/guia-de-compras-hoobuy-2026"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50 hover:text-accent-300'}`}
          >
            Guia de Compras
          </Link>

          <Link
            to="/artigo/guia-completo-hoobuyspreadsheet-2026"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50 hover:text-accent-300'}`}
          >
            Guia
          </Link>
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'border-primary-300 text-primary-600 hover:bg-primary-50'
                : 'border-background-50/40 text-background-50 hover:bg-background-50/20'
            }`}
          >
            Hoobuy Sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm font-semibold rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${scrolled ? 'text-foreground-900' : 'text-background-50'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Full Panel */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 font-medium hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-line text-lg text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              Início
            </Link>
            <Link
              to="/artigo/guia-de-compras-hoobuy-2026"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 font-medium hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-shopping-bag-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              Guia de Compras
            </Link>
            <Link
              to="/artigo/guia-completo-hoobuyspreadsheet-2026"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 font-medium hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-lg text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              Guia Completo
            </Link>

            {/* Divider */}
            <div className="border-t border-background-200 my-3"></div>

            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-foreground-400">
              Categorias
            </p>

            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <div className="w-8 h-8 rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                  </div>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="border-t border-background-200 mt-4 pt-4 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-all cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                Buy It Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium rounded-lg border border-foreground-200 text-foreground-700 hover:bg-background-100 transition-all cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                Hoobuy Sheet
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}